import React, { useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'

import { useTheme } from 'styled-components/native'

import { AlertBannerTypes } from '@lyrahealth-inc/shared-app-logic/src/features/alerts/AlertBannerTypes'
import { AttentionIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/AttentionIcon'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { AlertBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/alertBanner/AlertBanner'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import styles from './PageLoader.module.scss'
import usePersistedDataState from '../../../data/apiPersist/hooks/usePersistedDataState'
import { getAuthUserId } from '../../../data/auth/authSelectors'
import { useGetAppointmentsQuery } from '../../../lyraTherapy/clients/clientDetails/data/appointmentsApi'
import { getIsPrivacyGuardActive } from '../../../lyraTherapy/data/ltVideoSelectors'
import { SystemInterruptionModal } from '../systemInterruptionModal/SystemInterruptionModal'

const PageLoader: React.FC = () => {
  const { colors } = useTheme()
  const { isDataStale, hasError, lastUpdatedTime } = usePersistedDataState()
  const hasShownModal = useRef(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const userId = useSelector(getAuthUserId)
  const isPrivacyGuardActive = useSelector(getIsPrivacyGuardActive)
  const { data: appointments = [] } = useGetAppointmentsQuery({ providerId: userId }, { skip: !userId || !hasError })
  const { warning } = colors.components.alertBanner

  useEffect(() => {
    if (isPrivacyGuardActive) {
      setIsModalOpen(false)
      return
    }
    if (hasError && !hasShownModal.current) {
      hasShownModal.current = true
      setIsModalOpen(true)
    }
  }, [hasError, isPrivacyGuardActive])
  if (!isDataStale && !hasError) {
    return null
  }

  if (hasError) {
    return (
      <>
        <AlertBanner
          show
          style={AlertBannerTypes.WARNING}
          includeX
          minHeight='0px'
          icon={<AttentionIcon size={20} />}
          textSize={BodyTextSize.DEFAULT}
          contents={
            <FormattedMessage
              defaultMessage='Your information may not be up-to-date due to a connection issue.'
              description='Banner message when cached data is being served due to an error'
            />
          }
          trailingLink={
            isPrivacyGuardActive ? undefined : (
              <Link
                testID={tID('PageLoader-viewSchedule-link')}
                text={
                  <FormattedMessage
                    defaultMessage="View today's schedule"
                    description='Link text to open the system interruption modal'
                  />
                }
                onPress={() => setIsModalOpen(true)}
                color={warning.text}
                underline
              />
            )
          }
        />
        <SystemInterruptionModal
          isModalOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          lastUpdatedTime={lastUpdatedTime}
          appointments={appointments}
        />
      </>
    )
  }

  return (
    <div className={styles.outerContainer}>
      <div className={styles.loaderContainer} style={{ backgroundColor: colors.components.pageLoader.background }}>
        <div className={styles.bar} style={{ backgroundColor: colors.components.pageLoader.bar.background }} />
      </div>
    </div>
  )
}

export default PageLoader
